/**
 * Reconnecting WebSocket client wrapped around the protocol package.
 *
 *   - Maintains the connection, exponential backoff (capped 30s)
 *   - Parses every inbound frame as a ServerMessage
 *   - Drives the ClockSync helper
 *   - Re-emits validated messages to a subscriber
 */
import {
  encodeClientMessage,
  parseServerMessage,
  type ClientMessage,
  type ServerMessage,
} from '@syncstream/protocol';
import { ClockSync } from './clock.ts';

export type ServerMessageHandler = (msg: ServerMessage) => void;

export interface WsClientOptions {
  url: string;
  /** Called for every validated server message except clock_sync_response. */
  onMessage: ServerMessageHandler;
  onOpen?: () => void;
  onClose?: () => void;
  onError?: (err: unknown) => void;
  onClockUpdate?: (offsetMs: number) => void;
  /**
   * Test seam: substitute the WebSocket constructor. Used by integration tests
   * to inject a mock socket; production code leaves it undefined.
   */
  webSocketFactory?: (url: string) => WebSocket;
}

const RECONNECT_MIN_MS = 500;
const RECONNECT_MAX_MS = 30_000;

export class WsClient {
  private ws: WebSocket | null = null;
  private closed = false;
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly clock: ClockSync;

  constructor(private readonly opts: WsClientOptions) {
    this.clock = new ClockSync(
      (sendTime) => this.send({ type: 'clock_sync_request', client_send_time: sendTime }),
      opts.onClockUpdate,
    );
  }

  /** Server-clock estimate; returns Date.now() until the first clock-sync batch. */
  serverNow(): number {
    return this.clock.serverNow();
  }

  getClockOffset(): number {
    return this.clock.getOffset();
  }

  connect(): void {
    if (this.closed) return;
    if (
      this.ws &&
      (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }
    const ws = this.opts.webSocketFactory
      ? this.opts.webSocketFactory(this.opts.url)
      : new WebSocket(this.opts.url);
    this.ws = ws;
    ws.addEventListener('open', () => {
      this.reconnectAttempt = 0;
      this.opts.onOpen?.();
      void this.clock.runInitial();
    });
    ws.addEventListener('message', (ev) => {
      if (typeof ev.data !== 'string') return;
      const parsed = parseServerMessage(ev.data);
      if (!parsed.ok) {
        console.warn('ws: bad message', parsed.error);
        return;
      }
      if (parsed.msg.type === 'clock_sync_response') {
        this.clock.recordResponse(parsed.msg.client_send_time, parsed.msg.server_time);
        return;
      }
      this.opts.onMessage(parsed.msg);
    });
    ws.addEventListener('close', () => {
      this.opts.onClose?.();
      this.scheduleReconnect();
    });
    ws.addEventListener('error', (e) => {
      this.opts.onError?.(e);
    });
  }

  send(msg: ClientMessage): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(encodeClientMessage(msg));
  }

  close(): void {
    this.closed = true;
    this.clock.stop();
    if (this.reconnectTimer != null) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    if (this.ws) {
      try {
        this.ws.close();
      } catch {
        /* ignore */
      }
      this.ws = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.closed) return;
    if (this.reconnectTimer != null) return;
    const backoff = Math.min(
      RECONNECT_MAX_MS,
      RECONNECT_MIN_MS * 2 ** Math.min(this.reconnectAttempt, 7),
    );
    const jitter = Math.random() * 250;
    this.reconnectAttempt += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, backoff + jitter);
  }
}
